// Phase JB.2 — useEscalatingMenu hook.
//
// Returns the current user's tier-A unlock count for today, derived
// from escalating_menu_state.week_index. Anon → returns 0 unlocks.

import { useEffect, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { tierAUnlocksForWeek, unlockCopy } from "./escalatingMenu.ts";

export interface UseEscalatingMenuResult {
  weekIndex: number;
  tierAUnlocks: number;
  /** Celebratory copy (N08-compliant). */
  copy: string;
  loading: boolean;
}

export function useEscalatingMenu(): UseEscalatingMenuResult {
  const { user } = useAuthSession();
  const [weekIndex, setWeekIndex] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      setWeekIndex(1);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sb.from("escalating_menu_state" as never) as any)
      .select("week_index")
      .eq("user_id", user.id)
      .maybeSingle()
      .then((res: { data: { week_index: number } | null; error: unknown }) => {
        if (cancelled) return;
        setWeekIndex(res.data?.week_index ?? 1);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return {
    weekIndex,
    tierAUnlocks: tierAUnlocksForWeek(weekIndex),
    copy: unlockCopy(weekIndex),
    loading,
  };
}
